import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

const BackgroundContainer = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: -1,
  overflow: "hidden",
  background: "#0C173A",
  pointerEvents: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "200%",
    height: "200%",
    transform: "translate(-50%, -50%)",
    background:
      "radial-gradient(circle at center, rgba(0, 206, 209, 0.08) 0%, transparent 50%)",
    animation: "pulse 15s ease-in-out infinite",
  },
});

const SVGWrapper = styled("div")({
  position: "absolute",
  width: "100%",
  height: "100%",
  opacity: 0.75,
  pointerEvents: "none",
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-30px)",
    },
  },
  "@keyframes floatReverse": {
    "0%, 100%": {
      transform: "translateY(-20px)",
    },
    "50%": {
      transform: "translateY(20px)",
    },
  },
  "@keyframes floatSideways": {
    "0%, 100%": {
      transform: "translateX(-30px)",
    },
    "50%": {
      transform: "translateX(30px)",
    },
  },
  "@keyframes floatDiagonal": {
    "0%, 100%": {
      transform: "translate(-20px, -20px)",
    },
    "50%": {
      transform: "translate(20px, 20px)",
    },
  },
  "@keyframes rotate": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
  "@keyframes rotateReverse": {
    "0%": {
      transform: "rotate(360deg)",
    },
    "100%": {
      transform: "rotate(0deg)",
    },
  },
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 0.3,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.5,
      transform: "scale(1.05)",
    },
  },
  "@keyframes glowPulse": {
    "0%, 100%": {
      filter: "blur(2px) brightness(1)",
    },
    "50%": {
      filter: "blur(3px) brightness(1.3)",
    },
  },
  "@keyframes dash": {
    to: {
      strokeDashoffset: -1000,
    },
  },
});

const AnimatedBackground = () => {
  return (
    <BackgroundContainer>
      <SVGWrapper>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1000 1000"
          preserveAspectRatio="xMidYMid slice"
          style={{ position: "absolute" }}
        >
          <defs>
            <linearGradient
              id="techGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#00CED1" stopOpacity="0.35" />
              <stop offset="50%" stopColor="#4169E1" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#00CED1" stopOpacity="0.35" />
            </linearGradient>

            <pattern
              id="hexGrid"
              x="0"
              y="0"
              width="100"
              height="100"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M50 0L93.3 25V75L50 100L6.7 75V25L50 0z"
                fill="none"
                stroke="rgba(0, 206, 209, 0.15)"
                strokeWidth="0.5"
              />
            </pattern>

            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#00CED1" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#00CED1" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Base Hexagonal Grid - Now with rotation and pulse */}
          <g style={{ animation: "rotateReverse 180s linear infinite" }}>
            <rect width="100%" height="100%" fill="url(#hexGrid)" opacity="0.5">
              <animate
                attributeName="opacity"
                values="0.5;0.7;0.5"
                dur="10s"
                repeatCount="indefinite"
              />
            </rect>
          </g>

          {/* Floating Circles Layer - Increased count */}
          <g style={{ animation: "floatSideways 20s ease-in-out infinite" }}>
            {Array.from({ length: 25 }).map((_, i) => (
              <circle
                key={`float-circle-${i}`}
                cx={100 + ((i * 120) % 1000)}
                cy={100 + Math.floor((i * 120) / 1000) * 150}
                r={8 + (i % 4) * 4}
                fill="none"
                stroke="url(#techGradient)"
                strokeWidth="1.5"
                opacity="0.4"
                style={{
                  animation: `pulse ${8 + i}s ease-in-out infinite ${i}s`,
                }}
              />
            ))}
          </g>

          {/* Dynamic Wave Lines - Increased count and added variation */}
          <g style={{ animation: "float 25s ease-in-out infinite" }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <path
                key={`wave-${i}`}
                d={`M 0,${120 + i * 80} Q 250,${100 + i * 80} 500,${
                  120 + i * 80
                } T 1000,${120 + i * 80}`}
                fill="none"
                stroke="url(#techGradient)"
                strokeWidth="1"
                opacity="0.3"
                strokeDasharray="5,10"
              >
                <animate
                  attributeName="opacity"
                  values="0.3;0.5;0.3"
                  dur={`${15 + i * 2}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="strokeDashoffset"
                  values="0;1000"
                  dur={`${20 + i * 3}s`}
                  repeatCount="indefinite"
                />
              </path>
            ))}
          </g>

          {/* Floating Geometric Shapes - Increased count and added squares */}
          <g style={{ animation: "floatDiagonal 30s ease-in-out infinite" }}>
            {Array.from({ length: 20 }).map((_, i) => (
              <g
                key={`shape-${i}`}
                style={{ animation: `rotate ${20 + i * 5}s linear infinite` }}
              >
                {/* Triangles */}
                <path
                  d={`M ${150 + i * 100},${300 + (i % 3) * 80} l ${
                    15 + i * 2
                  },-${25 + i * 2} l ${15 + i * 2},${25 + i * 2} z`}
                  fill="none"
                  stroke="#00CED1"
                  strokeWidth="1"
                  opacity="0.25"
                  filter="url(#glow)"
                >
                  <animate
                    attributeName="opacity"
                    values="0.25;0.4;0.25"
                    dur={`${6 + i}s`}
                    repeatCount="indefinite"
                  />
                </path>

                {/* Hexagons */}
                <path
                  d={`M${250 + i * 80},${
                    150 + (i % 4) * 120
                  } l15,8 l15,0 l8,-15 l-8,-15 l-15,0 l-15,8 z`}
                  fill="none"
                  stroke="#4169E1"
                  strokeWidth="1"
                  opacity="0.3"
                  filter="url(#glow)"
                >
                  <animate
                    attributeName="opacity"
                    values="0.3;0.5;0.3"
                    dur={`${7 + i}s`}
                    repeatCount="indefinite"
                  />
                </path>

                {/* Squares */}
                <rect
                  x={200 + i * 90}
                  y={250 + (i % 3) * 100}
                  width={20 + (i % 3) * 5}
                  height={20 + (i % 3) * 5}
                  fill="none"
                  stroke="#00CED1"
                  strokeWidth="1"
                  opacity="0.25"
                  transform={`rotate(${45 + i * 10} ${210 + i * 90} ${
                    260 + (i % 3) * 100
                  })`}
                  filter="url(#glow)"
                >
                  <animate
                    attributeName="opacity"
                    values="0.25;0.4;0.25"
                    dur={`${8 + i}s`}
                    repeatCount="indefinite"
                  />
                </rect>
              </g>
            ))}
          </g>

          {/* Dynamic Mesh Points with Connections - Increased density */}
          <g style={{ animation: "floatReverse 35s ease-in-out infinite" }}>
            {Array.from({ length: 48 }).map((_, i) => (
              <g key={`mesh-${i}`}>
                <circle
                  cx={50 + (i % 8) * 150}
                  cy={50 + Math.floor(i / 8) * 150}
                  r="2"
                  fill="url(#dotGlow)"
                  filter="url(#glow)"
                >
                  <animate
                    attributeName="r"
                    values="2;4;2"
                    dur={`${3 + (i % 5)}s`}
                    repeatCount="indefinite"
                  />
                </circle>
                {i > 0 && (
                  <>
                    <line
                      x1={50 + (i % 8) * 150}
                      y1={50 + Math.floor(i / 8) * 150}
                      x2={50 + ((i - 1) % 8) * 150}
                      y2={50 + Math.floor((i - 1) / 8) * 150}
                      stroke="url(#techGradient)"
                      strokeWidth="1"
                      opacity="0.3"
                      strokeDasharray="5,10"
                    >
                      <animate
                        attributeName="opacity"
                        values="0.3;0.5;0.3"
                        dur={`${4 + (i % 3)}s`}
                        repeatCount="indefinite"
                      />
                    </line>
                    {i % 8 !== 0 && i > 8 && (
                      <line
                        x1={50 + (i % 8) * 150}
                        y1={50 + Math.floor(i / 8) * 150}
                        x2={50 + (i % 8) * 150}
                        y2={50 + (Math.floor(i / 8) - 1) * 150}
                        stroke="url(#techGradient)"
                        strokeWidth="1"
                        opacity="0.3"
                        strokeDasharray="5,10"
                      >
                        <animate
                          attributeName="opacity"
                          values="0.3;0.5;0.3"
                          dur={`${5 + (i % 4)}s`}
                          repeatCount="indefinite"
                        />
                      </line>
                    )}
                  </>
                )}
              </g>
            ))}
          </g>

          {/* Pulsing Energy Rings - Added more rings */}
          <g style={{ animation: "rotate 60s linear infinite" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <circle
                key={`ring-${i}`}
                cx="500"
                cy="500"
                r={80 + i * 45}
                fill="none"
                stroke="url(#techGradient)"
                strokeWidth="1"
                opacity="0.3"
                strokeDasharray={`${8 + i * 4},${16 + i * 8}`}
              >
                <animate
                  attributeName="opacity"
                  values="0.3;0.5;0.3"
                  dur={`${15 + i * 5}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="r"
                  values={`${80 + i * 45};${95 + i * 45};${80 + i * 45}`}
                  dur={`${15 + i * 5}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </g>

          {/* Glowing Accent Points - Increased count */}
          <g style={{ animation: "glowPulse 4s ease-in-out infinite" }}>
            {Array.from({ length: 40 }).map((_, i) => (
              <circle
                key={`accent-${i}`}
                cx={Math.random() * 1000}
                cy={Math.random() * 1000}
                r="1.5"
                fill="#00CED1"
                opacity="0.5"
                filter="url(#glow)"
              >
                <animate
                  attributeName="opacity"
                  values="0.5;0.8;0.5"
                  dur={`${2 + Math.random() * 3}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="r"
                  values="1.5;3;1.5"
                  dur={`${3 + Math.random() * 2}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </g>
        </svg>
      </SVGWrapper>
    </BackgroundContainer>
  );
};

export default AnimatedBackground;
